// Admin service for admin-only operations
import axios from "axios";
import { getApiBaseUrl, getHeaders, handleApiError } from "./apiClient";
import { requestWithAuth } from "./request";
import type { ScrapeJob } from "./ecommerceScrapeService";

// Using backend API response format (ok instead of success)
export type BackendApiResponse<T> = {
  ok: boolean;
  data?: T;
  error?: string;
  message?: string;
};

export type FirecrawlPlaygroundScrapeResult = {
  url: string;
  result: {
    markdown?: string;
    html?: string;
    metadata?: {
      title?: string;
      description?: string;
      language?: string;
      sourceURL?: string;
      ogTitle?: string;
      ogDescription?: string;
      ogImage?: string;
      "msapplication-TileImage"?: string;
      favicon?: string;
    };
    links?: string[];
  };
};

export type OllamaPlaygroundChatResult = {
  response: string;
  model: string;
  promptTokens: number;
  completionTokens: number;
  durationMs: number;
};

export type RedisCacheKeyRow = {
  key: string;
  ttlSeconds: number;
};

export type RedisCacheKeysResult = {
  pattern: string;
  total: number;
  keys: RedisCacheKeyRow[];
};

export type AdminStats = {
  totalUsers: number;
  totalAdmins: number;
  totalReviews: number;
  totalStores: number;
  totalScrapeJobs: number;
  recentUsers: Array<{
    _id: string;
    full_name?: string;
    email?: string;
    createdAt: string;
    isAdmin: boolean;
  }>;
};

export type UserWithPagination = {
  users: Array<{
    _id: string;
    full_name?: string;
    email?: string;
    phoneNumber?: string;
    isAdmin: boolean;
    provider?: string;
    points?: number;
    createdAt: string;
    updatedAt: string;
  }>;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalUsers: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
};

export type ReviewWithDetails = {
  _id: string;
  userId: {
    _id: string;
    full_name?: string;
    email?: string;
    isAdmin: boolean;
  };
  storeId: {
    _id: string;
    name: string;
    slug?: string;
  };
  rating: number;
  content: string;
  createdAt: string;
  updatedAt: string;
  // External review fields (e.g. shopify_widget / facebook_group reviews)
  isExternal?: boolean;
  externalSource?: string;
  externalAuthor?: string;
  externalDate?: string;
  overallRating?: number;
  text?: string;
  // Fake-campaign detector verdict. Flagged reviews stay in every list — the admin queue
  // shows the reasons and offers an unflag override.
  inauthentic?: {
    flagged: boolean;
    score: number;
    reasons: string[];
    burstWindowStart?: string;
    detectorVersion: number;
    flaggedAt: string;
    adminOverride?: { unflagged: boolean; at: string };
  };
};

export type ReviewWithPagination = {
  reviews: ReviewWithDetails[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalReviews: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
};

export type StoreWithDiscoverer = {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  status?: string;
  categoryId: string;
  category: {
    title: {
      ar: string;
      en: string;
    };
    slug: string;
  };
  location?: string;
  // Optional because Mongoose omits an all-empty nested object entirely: a store created with no
  // platform fields (e.g. the FB-review sentinel store) has no `platforms` key at all.
  platforms?: {
    facebook?: string;
    instagram?: string;
    whatsapp?: string;
    website?: string;
    phone?: string;
  };
  rating: number;
  totalReviews: number;
  logoUrl?: string;
  featured: boolean;
  verified: boolean;
  yetfarragRating?: number; // Internal rating (0-10) for sorting, hidden from users, visible to admins
  tags?: string[];
  searchAliases?: string[];
  paymentOptions?: string[];
  deliveryGovernorates?: string[];
  workingHours?: string;
  pinnedInCategories?: string[];
  googlePlaces?: {
    placeId: string;
    name: string;
    address: string;
    rating: number;
    reviewCount: number;
    photoUrl?: string;
    phoneNumber?: string;
    website?: string;
    priceLevel?: number;
    openingHours?: string[];
    geometry?: { lat: number; lng: number };
    types?: string[];
  };
  reviewSummary?: StoreReviewSummary;
  createdAt: string;
  updatedAt: string;
  discovererId?: {
    _id: string;
    full_name?: string;
    email?: string;
  };
};

export type StoreReviewSummary = {
  text: string;
  pros: string[];
  cons: string[];
  sentiment: "positive" | "mixed" | "negative";
  reviewCount: number;
  sources: {
    yetfarrag: number;
    facebook_group: number;
    google_maps: number;
  };
  generatedAt: string;
};

export type StoreForCuration = {
  _id: string;
  name: string;
  slug: string;
  logoUrl?: string;
  status?: string;
  rating: number;
  totalReviews: number;
  yetfarragRating?: number;
  featured?: boolean;
  pinnedInCategories?: string[];
  createdAt: string;
  _categoryRelevance?: number;
  _categoryScore?: number;
  platforms?: {
    website?: string;
    facebook?: string;
    instagram?: string;
    whatsapp?: string;
    phone?: string;
  };
};

export type StoreCurationPagination = {
  currentPage: number;
  totalPages: number;
  totalStores: number;
  hasNext: boolean;
  hasPrev: boolean;
};

export type StoreWithPagination = {
  stores: StoreWithDiscoverer[];
  draftCount: number;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalStores: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
};

// Update app settings (admin only)
export const updateAppSettings = async (settings: {
  auth?: { phoneEnabled?: boolean; claimOtpEnabled?: boolean };
}): Promise<BackendApiResponse<any>> => {
  try {
    const response = await axios.put(
      `${getApiBaseUrl()}/admin/settings`,
      settings,
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to update settings" };
  }
};

// Get admin dashboard statistics
export const getAdminStats = async (): Promise<
  BackendApiResponse<AdminStats>
> => {
  try {
    const response = await axios.get(`${getApiBaseUrl()}/admin/stats`, {
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to fetch admin stats" };
  }
};

// Get all users with pagination
export const getAllUsers = async ({
  page = 1,
  limit = 20,
  search,
}: {
  page?: number;
  limit?: number;
  search?: string;
} = {}): Promise<BackendApiResponse<UserWithPagination>> => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (search) {
      params.append("search", search);
    }

    const response = await axios.get(
      `${getApiBaseUrl()}/admin/users?${params}`,
      {
        headers: getHeaders(),
      }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to fetch users" };
  }
};

// Toggle user admin status
export const toggleUserAdminStatus = async ({
  userId,
  isAdmin,
}: {
  userId: string;
  isAdmin: boolean;
}): Promise<BackendApiResponse<{ message?: string }>> => {
  try {
    const response = await axios.patch(
      `${getApiBaseUrl()}/admin/users/${userId}/admin`,
      { isAdmin },
      {
        headers: getHeaders(),
      }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to update user admin status" };
  }
};

// Delete user (admin only)
export const deleteUser = async ({
  userId,
}: {
  userId: string;
}): Promise<BackendApiResponse<{ message?: string }>> => {
  try {
    const response = await axios.delete(
      `${getApiBaseUrl()}/admin/users/${userId}`,
      {
        headers: getHeaders(),
      }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to delete user" };
  }
};

// Get all reviews with admin details
export const getAllReviews = async ({
  page = 1,
  limit = 20,
  source,
  native,
}: {
  page?: number;
  limit?: number;
  source?: string;
  native?: boolean;
} = {}): Promise<
  BackendApiResponse<{
    reviews: ReviewWithDetails[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalReviews: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  }>
> => {
  try {
    const response = await requestWithAuth({
      path: `/admin/reviews`,
      method: "GET",
      params: {
        page,
        limit,
        ...(native ? { native: true } : {}),
        ...(source ? { source } : {}),
      },
    });

    return {
      ok: true,
      data: response as ReviewWithPagination,
    };
  } catch (error) {
    return { ok: false, error: "Failed to fetch reviews" };
  }
};

// Check if current user is admin
export const checkAdminStatus = async (): Promise<
  BackendApiResponse<boolean>
> => {
  try {
    const response = await requestWithAuth({
      path: `/users/admin-status`,
      method: "GET",
    });

    return {
      ok: true,
      data: response as boolean,
    };
  } catch (error) {
    return { ok: false, error: "Failed to check admin status" };
  }
};

// Get all stores for admin management
export const getAllStoresForAdmin = async ({
  page = 1,
  limit = 20,
  search,
  status,
}: {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'draft' | 'published';
} = {}): Promise<BackendApiResponse<StoreWithPagination>> => {
  try {
    const response = await requestWithAuth({
      path: `/admin/stores`,
      method: "GET",
      params: {
        page,
        limit,
        search,
        status,
      },
    });

    return { ok: true, data: response as StoreWithPagination };
  } catch (error) {
    return { ok: false, error: "Failed to fetch stores" };
  }
};

// Update store
export const updateStore = async ({
  storeId,
  storeData,
}: {
  storeId: string;
  storeData: Partial<StoreWithDiscoverer>;
}): Promise<BackendApiResponse<StoreWithDiscoverer>> => {
  try {
    const response = await axios.put(
      `${getApiBaseUrl()}/admin/stores/${storeId}`,
      storeData,
      {
        headers: getHeaders(),
      }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to update store" };
  }
};

// Upload store image (logo only — cover was retired from the product)
export const uploadStoreImage = async ({
  storeId,
  type,
  file,
}: {
  storeId: string;
  type: "logo";
  file: File;
}): Promise<BackendApiResponse<{ imageUrl: string }>> => {
  try {
    const formData = new FormData();
    formData.append("image", file);

    const headers = getHeaders();
    delete headers["Content-Type"];

    const response = await fetch(
      `${getApiBaseUrl()}/admin/stores/${storeId}/image?type=${type}`,
      {
        method: "POST",
        headers,
        body: formData,
      }
    );

    const data = await response.json();
    if (!response.ok) {
      return { ok: false, error: data?.error || "Failed to upload image" };
    }
    return data;
  } catch (error) {
    return { ok: false, error: "Failed to upload image" };
  }
};

// Rescrape store
export const rescrapeStore = async ({
  storeId,
}: {
  storeId: string;
}): Promise<BackendApiResponse<{ jobId?: string; status?: string }>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}/admin/stores/${storeId}/rescrape`,
      {},
      {
        headers: getHeaders(),
      }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to rescrape store" };
  }
};

// Provision demo widget config (idempotent — returns created: false on re-run)
export const provisionWidgetConfig = async ({
  storeId,
}: {
  storeId: string;
}): Promise<BackendApiResponse<{ config?: unknown; created?: boolean }>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}/admin/stores/${storeId}/provision-widget-config`,
      {},
      {
        headers: getHeaders(),
      }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to provision widget config" };
  }
};

// Delete store
export const deleteStore = async ({
  storeId,
  deleteGoogleMapsReviews = true,
}: {
  storeId: string;
  deleteGoogleMapsReviews?: boolean;
}): Promise<BackendApiResponse<{ message?: string }>> => {
  try {
    const response = await axios.delete(
      `${getApiBaseUrl()}/admin/stores/${storeId}`,
      {
        headers: getHeaders(),
        params: { deleteGoogleMapsReviews },
      }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to delete store" };
  }
};

// Category management types
export type CategoryStatus = 'active' | 'pending' | 'merged';

export type CategoryWithStoreCount = {
  id: string;
  title: {
    ar: string;
    en: string;
  };
  description: string;
  slug: string;
  storeCount: number;
  // Published stores only — this is the count the promotion threshold keys on.
  publishedStoreCount: number;
  // 'pending' = on probation, hidden from public browse until it earns enough
  // published stores. 'merged' = tombstone kept so the slug 301s instead of 404ing.
  status: CategoryStatus;
  mergedInto?: string;
};

// Keep in sync with PROMOTION_MIN_PUBLISHED_STORES in the backend categoriesService.
export const CATEGORY_PROMOTION_THRESHOLD = 3;

// Get all categories with store counts
export const getAllCategoriesForAdmin = async (): Promise<BackendApiResponse<CategoryWithStoreCount[]>> => {
  try {
    const response = await axios.get(
      `${getApiBaseUrl()}/admin/categories`,
      {
        headers: getHeaders(),
      }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to fetch categories" };
  }
};

// Create category
export const createCategory = async ({
  title,
  description,
  slug,
}: {
  title: { ar: string; en: string };
  description: string;
  slug: string;
}): Promise<BackendApiResponse<{ id: string; title: { ar: string; en: string }; description: string; slug: string }>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}/admin/categories`,
      { title, description, slug },
      {
        headers: getHeaders(),
      }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to create category" };
  }
};

// Update category
export const updateCategory = async ({
  categoryId,
  title,
  description,
  slug,
}: {
  categoryId: string;
  title: { ar: string; en: string };
  description: string;
  slug: string;
}): Promise<BackendApiResponse<{ id: string; title: { ar: string; en: string }; description: string; slug: string }>> => {
  try {
    const response = await axios.put(
      `${getApiBaseUrl()}/admin/categories/${categoryId}`,
      { title, description, slug },
      {
        headers: getHeaders(),
      }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to update category" };
  }
};

// Delete category
export const deleteCategory = async ({
  categoryId,
}: {
  categoryId: string;
}): Promise<BackendApiResponse<{ deletedStores: number }>> => {
  try {
    const response = await axios.delete(
      `${getApiBaseUrl()}/admin/categories/${categoryId}`,
      {
        headers: getHeaders(),
      }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to delete category" };
  }
};

// AI Operation Log types
export type AIOperationLog = {
  _id: string;
  operationType: "category_inference" | "scraping" | "store_creation" | "ecommerce_scrape";
  entityType: string;
  entityId?: string;
  entityName?: string;
  input: Record<string, any>;
  output: Record<string, any>;
  rationale?: string;
  status: "pending" | "completed" | "failed";
  error?: string;
  cost?: {
    totalInputTokens: number;
    totalOutputTokens: number;
    totalCostUsd: number;
    llmCalls: number;
  };
  review: {
    status: "pending" | "approved" | "rejected" | "overridden";
    reviewedBy?: { _id: string; full_name?: string; email?: string };
    reviewedAt?: string;
    overrideData?: Record<string, any>;
    notes?: string;
  };
  createdAt: string;
  updatedAt: string;
};

export type AILogsWithPagination = {
  logs: AIOperationLog[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalLogs: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
};

// Get AI operation logs
export const getAIOperationLogs = async ({
  page = 1,
  limit = 20,
  operationType,
  reviewStatus,
  status,
}: {
  page?: number;
  limit?: number;
  operationType?: string;
  reviewStatus?: string;
  status?: string;
} = {}): Promise<BackendApiResponse<AILogsWithPagination>> => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (operationType) params.append("operationType", operationType);
    if (reviewStatus) params.append("reviewStatus", reviewStatus);
    if (status) params.append("status", status);

    const response = await axios.get(
      `${getApiBaseUrl()}/admin/ai-logs?${params}`,
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to fetch AI operation logs" };
  }
};

// Review an AI operation log
export const reviewAIOperationLog = async ({
  logId,
  status,
  notes,
  overrideData,
}: {
  logId: string;
  status: "approved" | "rejected" | "overridden";
  notes?: string;
  overrideData?: Record<string, any>;
}): Promise<BackendApiResponse<AIOperationLog>> => {
  try {
    const response = await axios.patch(
      `${getApiBaseUrl()}/admin/ai-logs/${logId}/review`,
      { status, notes, overrideData },
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to review AI operation log" };
  }
};

// Recalculate categories for a specific store
export const recalculateStoreCategories = async ({
  storeId,
}: {
  storeId: string;
}): Promise<BackendApiResponse<{
  categories: Array<{
    categoryId: string;
    category: {
      title: { en: string; ar: string };
      slug: string;
    };
    relevanceScore: number;
  }>;
  prominentCategory: {
    categoryId: string;
    category: {
      title: { en: string; ar: string };
      slug: string;
    };
    relevanceScore: number;
  };
  tags: string[];
}>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}/admin/stores/${storeId}/recalculate-categories`,
      {},
      {
        headers: getHeaders(),
      }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to recalculate store categories" };
  }
};

// Generate (or regenerate) the LLM review summary for a store
export const generateStoreReviewSummary = async ({
  storeId,
}: {
  storeId: string;
}): Promise<BackendApiResponse<StoreReviewSummary>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}/admin/stores/${storeId}/review-summary`,
      {},
      {
        headers: getHeaders(),
      }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to generate review summary" };
  }
};

// ─── Review authenticity audit (fake FB-page campaign detector) ──────────────────────────

export type ReviewAuditSweepReport = {
  // Whole-corpus sweep shape
  storesScanned?: number;
  storesFlagged?: number;
  reviewsFlagged?: number;
  perStore?: Array<{ storeId: string; flagged: number; status: string }>;
  // Single-store sweep shape
  storeId?: string;
  flagged?: number;
  unflagged?: number;
  audit?: {
    status: string;
    flaggedCount: number;
    totalFacebookReviews: number;
    maxBurst1h: number;
    burstFraction: number;
    otherSourceAverage: number | null;
  };
};

/**
 * Run the detector. `dryRun: true` reports the verdict without writing anything — the admin
 * UI always previews first and only re-runs live after the operator confirms.
 */
export const runReviewAuthenticitySweep = async ({
  dryRun = true,
  storeId,
}: {
  dryRun?: boolean;
  storeId?: string;
} = {}): Promise<BackendApiResponse<ReviewAuditSweepReport>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}/admin/review-audit/sweep`,
      { dryRun, ...(storeId ? { storeId } : {}) },
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to run authenticity sweep" };
  }
};

/** Permanent per-review override — a later sweep may never re-flag it. */
export const unflagReviewAuthenticity = async ({
  reviewId,
}: {
  reviewId: string;
}): Promise<BackendApiResponse<{ reviewId: string; storeId: string }>> => {
  try {
    const response = await axios.put(
      `${getApiBaseUrl()}/admin/review-audit/reviews/${reviewId}/unflag`,
      {},
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to unflag review" };
  }
};

/** Clears every authenticity flag on one store. */
export const unflagStoreAuthenticity = async ({
  storeId,
}: {
  storeId: string;
}): Promise<BackendApiResponse<{ storeId: string; unflagged: number }>> => {
  try {
    const response = await axios.put(
      `${getApiBaseUrl()}/admin/review-audit/stores/${storeId}/unflag`,
      {},
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to unflag store reviews" };
  }
};

export type GooglePlaceResult = {
  placeId: string;
  name: string;
  address: string;
  rating: number;
  reviewCount: number;
  photoUrl?: string;
  phoneNumber?: string;
  website?: string;
  priceLevel?: number;
  geometry?: { lat: number; lng: number };
  types?: string[];
};

export const searchGooglePlaces = async ({
  q,
  location,
}: {
  q: string;
  location?: string;
}): Promise<BackendApiResponse<GooglePlaceResult[]>> => {
  try {
    const params = new URLSearchParams({ q });
    if (location) params.append("location", location);
    const response = await axios.get(
      `${getApiBaseUrl()}/admin/stores/google-places/search?${params}`,
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to search Google Places" };
  }
};

export const updateStoreGooglePlace = async ({
  storeId,
  placeId,
  removeOldReviews,
  importNewReviews,
}: {
  storeId: string;
  placeId?: string;
  removeOldReviews?: boolean;
  importNewReviews?: boolean;
}): Promise<BackendApiResponse<{ placeId: string | null; placeName?: string; removedCount: number; imported: number; skipped: number }>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}/admin/stores/${storeId}/google-places`,
      { placeId, removeOldReviews, importNewReviews },
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to update store Google Place" };
  }
};

// ─── Store branches (Phase 32, D-12) ───
// The seven admin branch endpoints. All are mounted under the admin router's global `adminAuth`
// (T-32-08) — this client performs no authorization decision of its own, it only sends getHeaders().

// One row of the resolve preview. `branch` is absent when confidence === 'failed'.
// `reason` is authored server-side and must be rendered verbatim: beyond the three failure
// strings in the UI spec, the resolver also emits low-confidence `matched` reasons.
export type ResolvedBranchCandidate = {
  link: string;
  confidence: 'direct' | 'matched' | 'failed';
  reason?: string;
  distanceMeters?: number;
  branch?: {
    name: string;
    googlePlaceId: string;
    address?: string;
    governorate?: string;
    phone?: string;
    workingHours?: string[];
    geometry?: { lat: number; lng: number };
    mapsUrl?: string;
  };
};

// The FULL stored branch echoed back by GET .../branches/stats, plus the computed diagnostics.
// This is the admin UI's ONLY source of the stored branch array — `getAllStoresForAdmin` uses an
// inclusion projection that omits `branches`, and `PUT .../branches` is a whole-array replace, so
// the round-trip of the stored fields here is load-bearing (T-32-41).
export type BranchStatsRow = {
  // stored branch fields
  name: string;
  googlePlaceId?: string;
  isPrimary?: boolean;
  address?: string;
  governorate?: string;
  phone?: string;
  workingHours?: string[];
  geometry?: { lat: number; lng: number };
  mapsUrl?: string;
  // computed / diagnostic (never resubmitted — the server re-merges its own copy)
  importedCount: number;
  importedRating: number | null;
  googleRating?: number;
  googleReviewCount?: number;
  googleStatsCheckedAt?: string;
  reviewsSyncedAt?: string;
  available: number;
  stale?: boolean;
};

// The branch object shape accepted by PUT .../branches (stored fields only).
export type StoreBranchInput = {
  name: string;
  googlePlaceId?: string;
  isPrimary?: boolean;
  address?: string;
  governorate?: string;
  phone?: string;
  workingHours?: string[];
  geometry?: { lat: number; lng: number };
  mapsUrl?: string;
};

// Resolve pasted Maps links into candidates. WRITES NOTHING (D-10) — the explicit
// saveStoreBranches call is the only write path out of the preview.
export const resolveStoreBranchLinks = async ({
  storeId,
  links,
}: {
  storeId: string;
  links: string[];
}): Promise<BackendApiResponse<ResolvedBranchCandidate[]>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}/admin/stores/${storeId}/branches/resolve`,
      { links },
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to resolve branch links" };
  }
};

// Whole-array replace — the payload must always carry the existing branches too.
export const saveStoreBranches = async ({
  storeId,
  branches,
}: {
  storeId: string;
  branches: StoreBranchInput[];
}): Promise<BackendApiResponse<{ branches: StoreBranchInput[]; primaryPlaceId?: string }>> => {
  try {
    const response = await axios.put(
      `${getApiBaseUrl()}/admin/stores/${storeId}/branches`,
      { branches },
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to save branches" };
  }
};

// `importingPlaceIds` rides on every response: the branches currently mid background review
// import — the UI disables exactly those rows and polls this endpoint until the list drains.
export const getStoreBranchStats = async ({
  storeId,
}: {
  storeId: string;
}): Promise<BackendApiResponse<BranchStatsRow[]> & { importingPlaceIds?: string[] }> => {
  try {
    const response = await axios.get(
      `${getApiBaseUrl()}/admin/stores/${storeId}/branches/stats`,
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to load branch stats" };
  }
};

// CHEAP (D-18): one minimal-field-mask Places lookup per branch, no Apify actor run.
export const refreshStoreBranchStats = async ({
  storeId,
}: {
  storeId: string;
}): Promise<BackendApiResponse<{ refreshed: number; stale: string[] }>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}/admin/stores/${storeId}/branches/refresh-stats`,
      {},
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to refresh branch stats" };
  }
};

// EXPENSIVE (D-18/T-32-06): every branch processed is a billed Apify actor run.
// The server answers 202 immediately and runs the import in the background (each branch is a
// ~1-minute actor run; a synchronous response used to die on Heroku's 30s router timeout).
export const importStoreBranchReviews = async ({
  storeId,
  placeIds,
}: {
  storeId: string;
  placeIds?: string[];
}): Promise<BackendApiResponse<{ started: boolean; branches: number }>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}/admin/stores/${storeId}/branches/import-reviews`,
      placeIds === undefined ? {} : { placeIds },
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    // A 409 (an import is already running for this store) carries a meaningful server message —
    // surface it instead of the generic fallback.
    const data = axios.isAxiosError(error)
      ? (error.response?.data as { ok?: boolean; error?: string } | undefined)
      : undefined;
    return data?.error ? { ok: false, error: data.error } : { ok: false, error: "Failed to import branch reviews" };
  }
};

export const deleteStoreBranchReviews = async ({
  storeId,
  placeId,
}: {
  storeId: string;
  placeId: string;
}): Promise<BackendApiResponse<{ affected: number; branchName?: string; promotedPlaceId?: string }>> => {
  try {
    const response = await axios.delete(
      `${getApiBaseUrl()}/admin/stores/${storeId}/branches/${encodeURIComponent(placeId)}/reviews`,
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to delete branch reviews" };
  }
};

export const deleteStoreBranch = async ({
  storeId,
  placeId,
}: {
  storeId: string;
  placeId: string;
}): Promise<BackendApiResponse<{ affected: number; branchName?: string; promotedPlaceId?: string }>> => {
  try {
    const response = await axios.delete(
      `${getApiBaseUrl()}/admin/stores/${storeId}/branches/${encodeURIComponent(placeId)}`,
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to delete branch" };
  }
};

// Lightweight draft store reference for review page navigation
export type DraftStoreRef = {
  _id: string;
  name: string;
  slug: string;
  createdAt: string;
};

// Get lightweight list of all draft stores for navigation
export const getDraftStoreList = async (): Promise<
  BackendApiResponse<DraftStoreRef[]>
> => {
  try {
    const response = await axios.get(
      `${getApiBaseUrl()}/admin/stores/drafts/list`,
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to fetch draft store list" };
  }
};

// Get the scrape job associated with a draft store
export const getScrapeJobForStore = async ({
  storeId,
}: {
  storeId: string;
}): Promise<BackendApiResponse<ScrapeJob | null>> => {
  try {
    const response = await axios.get(
      `${getApiBaseUrl()}/admin/stores/${storeId}/scrape-job`,
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to fetch scrape job for store" };
  }
};

export const testFirecrawlScrape = async ({
  url,
}: {
  url: string;
}): Promise<BackendApiResponse<FirecrawlPlaygroundScrapeResult>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}/admin/playground/firecrawl/scrape`,
      { url },
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return { ok: false, error: error.response?.data?.error || "Failed to test Firecrawl scrape" };
    }
    const handled = handleApiError(error);
    return { ok: false, error: handled.message || "Failed to test Firecrawl scrape" };
  }
};

export const testOllamaChat = async ({
  prompt,
  systemPrompt,
  model,
}: {
  prompt: string;
  systemPrompt?: string;
  model?: string;
}): Promise<BackendApiResponse<OllamaPlaygroundChatResult>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}/admin/playground/ollama/chat`,
      { prompt, systemPrompt, model },
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const baseError = error.response?.data?.error || "Failed to test Ollama chat";
      const debugBaseUrl = error.response?.data?.data?.debug?.baseUrl;
      const debugHasApiKey = error.response?.data?.data?.debug?.hasApiKey;
      if (debugBaseUrl) {
        return {
          ok: false,
          error: `${baseError} (baseUrl: ${debugBaseUrl}, hasApiKey: ${String(debugHasApiKey)})`,
        };
      }
      return { ok: false, error: baseError };
    }
    const handled = handleApiError(error);
    return { ok: false, error: handled.message || "Failed to test Ollama chat" };
  }
};

export const listRedisCacheKeys = async ({
  pattern = "apify:cache:*",
  limit = 200,
}: {
  pattern?: string;
  limit?: number;
} = {}): Promise<BackendApiResponse<RedisCacheKeysResult>> => {
  try {
    const params = new URLSearchParams({
      pattern,
      limit: String(limit),
    });
    const response = await axios.get(
      `${getApiBaseUrl()}/admin/playground/redis/cache-keys?${params}`,
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return { ok: false, error: error.response?.data?.error || "Failed to list Redis cache keys" };
    }
    const handled = handleApiError(error);
    return { ok: false, error: handled.message || "Failed to list Redis cache keys" };
  }
};

// Get stores in a category for curation (pinned first, then by yetfarragRating)
export const getCategoryStoresForAdmin = async ({
  slug,
  page = 1,
  limit = 50,
  sort,
}: {
  slug: string;
  page?: number;
  limit?: number;
  sort?: string;
}): Promise<BackendApiResponse<{ stores: StoreForCuration[]; pagination: StoreCurationPagination }>> => {
  try {
    const response = await axios.get(
      `${getApiBaseUrl()}/admin/categories/${encodeURIComponent(slug)}/stores`,
      { headers: getHeaders(), params: { page, limit, ...(sort ? { sort } : {}) } }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to fetch category stores" };
  }
};

// Remove a store from a category (admin curation)
export const removeStoreFromCategory = async ({
  slug,
  storeId,
}: {
  slug: string;
  storeId: string;
}): Promise<BackendApiResponse<{ storeId: string }>> => {
  try {
    const response = await axios.delete(
      `${getApiBaseUrl()}/admin/categories/${encodeURIComponent(slug)}/stores/${storeId}`,
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to remove store from category" };
  }
};

// Update a store's per-category prominence (relevanceScore 1-10)
export const updateStoreCategoryRelevance = async ({
  slug,
  storeId,
  relevanceScore,
}: {
  slug: string;
  storeId: string;
  relevanceScore: number;
}): Promise<BackendApiResponse<{ storeId: string; relevanceScore: number }>> => {
  try {
    const response = await axios.patch(
      `${getApiBaseUrl()}/admin/categories/${encodeURIComponent(slug)}/stores/${storeId}/relevance`,
      { relevanceScore },
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to update prominence" };
  }
};

// Batch-apply category curation edits (relevance / YF / pin / remove) in one PATCH.
// Backend is per-change resilient: returns { applied, failed } — not all-or-nothing.
export const batchUpdateCategoryStores = async ({
  slug,
  changes,
}: {
  slug: string;
  changes: {
    storeId: string;
    relevanceScore?: number;
    yetfarragRating?: number;
    pinned?: boolean;
    remove?: boolean;
  }[];
}): Promise<BackendApiResponse<{ applied: number; failed: { storeId: string; error: string }[] }>> => {
  try {
    const response = await axios.patch(
      `${getApiBaseUrl()}/admin/categories/${encodeURIComponent(slug)}/stores/batch`,
      { changes },
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to batch update category stores" };
  }
};
